'use client';

import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SocialHandleInput } from '@kit/artists';
import { Checkbox } from '@kit/ui/checkbox';
import { DateField as KitDateField } from '@kit/ui/date-field';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { Textarea } from '@kit/ui/textarea';

import { resolveDancerDivision } from '../../lib/age';
import { socialPlatformForFieldKey } from '../../lib/social-handle-field';
import type { ApplicationFieldConfig } from '../../types/audition.types';
import { AGE_DIVISION_LABELS } from '../../types/competition-category.types';
import {
  buildRequiredRule,
  type AgeCategoryContext,
} from './dynamic-form-renderer-shared';

type Form = UseFormReturn<Record<string, unknown>>;

/**
 * Read-only age-category field. Watches the date-of-birth answer and derives
 * the dancer's age division (`resolveDancerDivision`) reactively — it is never
 * user-editable. Rendered immediately after the DOB field by the structure
 * ordering. Shows a hint to add a date of birth when none is present.
 */
export function AgeCategoryField({
  field,
  form,
  context,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  context?: AgeCategoryContext;
}): React.ReactNode {
  const { t } = useTranslation();
  const dobFieldKey = context?.dobFieldKey ?? 'birthdate';
  // Reactive: recompute whenever the DOB answer changes.
  const dob = form.watch(dobFieldKey);
  const division = resolveDancerDivision({
    dateOfBirth: typeof dob === 'string' && dob.length > 0 ? dob : null,
    ageCutDate: context?.ageCutDate ?? null,
    firstEventDate: context?.firstEventDate ?? null,
  });

  // NOTE: `age_category` is a DERIVED, read-only field — it is never registered
  // with react-hook-form (no <FormField>/Controller). The @kit/ui `FormLabel` /
  // `FormDescription` components call `useFormField()`, which THROWS
  // ("useFormField should be used within <FormField>") when rendered outside a
  // FormField context. So this field uses plain markup (label / p) that mirrors
  // FormLabel/FormDescription styling instead of the context-bound components.
  return (
    <div className="space-y-2">
      <label className="text-sm leading-none font-medium">{field.label}</label>
      <div
        role="status"
        aria-live="polite"
        className="border-input bg-muted text-foreground/90 flex h-10 items-center rounded-md border px-3 text-sm"
      >
        {division ? (
          <span className="font-medium">{AGE_DIVISION_LABELS[division]}</span>
        ) : (
          <span className="text-muted-foreground">
            {t('auditions:formRenderer.ageCategory.noDob')}
          </span>
        )}
      </div>
      <p className="text-muted-foreground text-sm">
        {field.description ?? t('auditions:formRenderer.ageCategory.autoHint')}
      </p>
    </div>
  );
}

export function HeadingField({
  field,
}: {
  field: ApplicationFieldConfig;
}): React.ReactNode {
  return (
    <div className="col-span-full pt-4 first:pt-0">
      <h3 className="text-lg font-semibold">{field.label}</h3>
      {field.description && (
        <p className="text-muted-foreground text-sm">{field.description}</p>
      )}
    </div>
  );
}

export function ParagraphField({
  field,
}: {
  field: ApplicationFieldConfig;
}): React.ReactNode {
  return (
    <div className="bg-muted/50 col-span-full rounded-lg p-4">
      <p className="text-muted-foreground text-sm">{field.label}</p>
    </div>
  );
}

export function TextInputField({
  field,
  form,
  requiredMessage,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
}): React.ReactNode {
  // Upgrade a plain social-handle text field (e.g. `instagramUsername`) to the
  // shared SocialHandleInput. Verification is disabled here: the apply form is
  // high-volume / often unauthenticated, so we keep the icon/prefix/normalize/
  // link-out UX without firing the oEmbed lookup.
  const socialPlatform =
    field.type === 'text' || field.type === 'url'
      ? socialPlatformForFieldKey(field.key)
      : null;

  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          <FormControl>
            {socialPlatform ? (
              <SocialHandleInput
                platform={socialPlatform}
                showLabel={false}
                enableVerification={false}
                value={(formField.value as string) || ''}
                onChange={formField.onChange}
                onBlur={formField.onBlur}
              />
            ) : (
              <Input
                type={
                  field.type === 'email'
                    ? 'email'
                    : field.type === 'url'
                      ? 'url'
                      : field.type === 'phone'
                        ? 'tel'
                        : 'text'
                }
                placeholder={field.placeholder}
                {...formField}
                value={(formField.value as string) || ''}
              />
            )}
          </FormControl>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function NumberField({
  field,
  form,
  requiredMessage,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
}): React.ReactNode {
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          <FormControl>
            <Input
              type="number"
              placeholder={field.placeholder}
              min={field.validation?.min}
              max={field.validation?.max}
              {...formField}
              value={(formField.value as number) ?? ''}
              onChange={(e) =>
                formField.onChange(
                  e.target.value ? parseFloat(e.target.value) : null,
                )
              }
            />
          </FormControl>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function TextareaField({
  field,
  form,
  requiredMessage,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
}): React.ReactNode {
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          <FormControl>
            <Textarea
              placeholder={field.placeholder}
              rows={4}
              {...formField}
              value={(formField.value as string) || ''}
            />
          </FormControl>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function DateField({
  field,
  form,
  requiredMessage,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
}): React.ReactNode {
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {field.label}
            {field.validation?.required && (
              <span className="text-destructive ml-1">*</span>
            )}
          </FormLabel>
          <FormControl>
            <KitDateField
              name={formField.name}
              value={(formField.value as string) || ''}
              onChange={formField.onChange}
              clearable
            />
          </FormControl>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function CheckboxField({
  field,
  form,
  requiredMessage,
}: {
  field: ApplicationFieldConfig;
  form: Form;
  requiredMessage: string;
}): React.ReactNode {
  return (
    <FormField
      control={form.control}
      name={field.key}
      rules={buildRequiredRule(field, requiredMessage)}
      render={({ field: formField }) => (
        <FormItem className="flex flex-row items-start space-y-0 space-x-3 rounded-md border p-4">
          <FormControl>
            <Checkbox
              checked={formField.value as boolean}
              onCheckedChange={formField.onChange}
            />
          </FormControl>
          <div className="space-y-1 leading-none">
            <FormLabel>
              {field.label}
              {field.validation?.required && (
                <span className="text-destructive ml-1">*</span>
              )}
            </FormLabel>
            {field.description && (
              <FormDescription>{field.description}</FormDescription>
            )}
          </div>
        </FormItem>
      )}
    />
  );
}
